import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Image as ImageIcon,
  Upload,
  Trash2,
  Search,
  X,
  Check,
  Edit,
  Copy,
  ExternalLink,
  FileImage,
  Loader2,
  AlertCircle,
  CheckCircle,
  Images
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { MediaRestorePanel } from './MediaRestorePanel';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminMediaLibrary = ({ pickerMode = false, onSelect = null }) => {
  const { token, user } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isNewUpload, setIsNewUpload] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);
  
  // Multi-file upload state
  const [uploadQueue, setUploadQueue] = useState([]); // Files waiting to upload
  const [uploadProgress, setUploadProgress] = useState({}); // Progress per file
  const [isUploading, setIsUploading] = useState(false);
  const [recentlyUploaded, setRecentlyUploaded] = useState([]); // IDs of recently uploaded images

  // Edit form state
  const [editForm, setEditForm] = useState({
    title: '',
    alt_text: '',
    description: ''
  });

  const fetchImages = useCallback(async () => {
    try {
      setLoading(true);
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const response = await axios.get(`${API}/media${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(response.data);
    } catch (error) {
      console.error('Feil ved henting av bilder:', error);
    } finally {
      setLoading(false);
    }
  }, [token, searchQuery]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchImages();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchImages]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFilesSelect(files);
    }
  };

  const handleFilesSelect = (files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      if (allowedTypes.includes(file.type)) {
        validFiles.push({
          file,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: file.name,
          preview: URL.createObjectURL(file),
          status: 'pending' // pending, uploading, success, error
        });
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      toast({
        title: "Ugyldige filer",
        description: `${invalidFiles.join(', ')} er ikke støttet. Bruk JPG, PNG, GIF eller WEBP.`,
        variant: "destructive"
      });
    }

    if (validFiles.length > 0) {
      setUploadQueue(prev => [...prev, ...validFiles]);
    }
  };

  // Process upload queue
  useEffect(() => {
    const uploadNext = async () => {
      const pendingFile = uploadQueue.find(f => f.status === 'pending');
      if (!pendingFile || isUploading) return;

      setIsUploading(true);
      
      // Update status to uploading
      setUploadQueue(prev => prev.map(f => 
        f.id === pendingFile.id ? { ...f, status: 'uploading' } : f
      ));

      try {
        const formData = new FormData();
        formData.append('file', pendingFile.file);
        formData.append('title', pendingFile.name.replace(/\.[^/.]+$/, ''));
        formData.append('alt_text', pendingFile.name.replace(/\.[^/.]+$/, ''));

        const response = await axios.post(`${API}/media/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [pendingFile.id]: percentCompleted }));
          }
        });

        // Update status to success
        setUploadQueue(prev => prev.map(f => 
          f.id === pendingFile.id ? { ...f, status: 'success', uploadedId: response.data.id } : f
        ));

        // Add to recently uploaded
        setRecentlyUploaded(prev => [response.data.id, ...prev]);

        // Refresh the image list - inline to avoid dependency issues
        try {
          const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
          const imagesResponse = await axios.get(`${API}/media${params}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setImages(imagesResponse.data);
        } catch (imgError) {
          console.error('Feil ved henting av bilder:', imgError);
        }

      } catch (error) {
        console.error('Feil ved opplasting:', error);
        setUploadQueue(prev => prev.map(f => 
          f.id === pendingFile.id ? { ...f, status: 'error', error: error.response?.data?.detail || 'Opplasting feilet' } : f
        ));
      } finally {
        setIsUploading(false);
      }
    };

    uploadNext();
  }, [uploadQueue, isUploading, token, searchQuery]);

  // Clear recently uploaded highlight after 10 seconds
  useEffect(() => {
    if (recentlyUploaded.length > 0) {
      const timer = setTimeout(() => {
        setRecentlyUploaded([]);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [recentlyUploaded]);

  const removeFromQueue = (id) => {
    setUploadQueue(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== id);
    });
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[id];
      return newProgress;
    });
  };

  const clearCompletedUploads = () => {
    setUploadQueue(prev => {
      prev.forEach(f => {
        if (f.preview && (f.status === 'success' || f.status === 'error')) {
          URL.revokeObjectURL(f.preview);
        }
      });
      return prev.filter(f => f.status !== 'success' && f.status !== 'error');
    });
  };

  const hasActiveUploads = uploadQueue.some(f => f.status === 'pending' || f.status === 'uploading');
  const completedUploads = uploadQueue.filter(f => f.status === 'success').length;
  const failedUploads = uploadQueue.filter(f => f.status === 'error').length;

  const handleEditOpen = (image) => {
    setSelectedImage(image);
    setEditForm({
      title: image.title || '',
      alt_text: image.alt_text || '',
      description: image.description || ''
    });
    setIsNewUpload(false); // Not a new upload, regular edit
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    if (!selectedImage) return;

    try {
      await axios.put(`${API}/media/${selectedImage.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditModalOpen(false);
      fetchImages();
    } catch (error) {
      console.error('Feil ved oppdatering:', error);
      alert(error.response?.data?.detail || 'Kunne ikke oppdatere metadata');
    }
  };

  const handleDelete = async () => {
    if (!selectedImage) return;

    try {
      await axios.delete(`${API}/media/${selectedImage.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteConfirmOpen(false);
      setSelectedImage(null);
      fetchImages();
    } catch (error) {
      console.error('Feil ved sletting:', error);
      alert(error.response?.data?.detail || 'Kunne ikke slette bilde');
    }
  };

  const copyToClipboard = async (url, size) => {
    const fullUrl = `${window.location.origin}${url}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedUrl(`${selectedImage?.id}-${size}`);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (err) {
      console.error('Kunne ikke kopiere:', err);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const handleImageSelect = (image) => {
    if (pickerMode && onSelect) {
      onSelect(image);
    } else {
      handleEditOpen(image);
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Progress Bar - Fixed at top when uploading */}
      {uploadQueue.length > 0 && (
        <Card className="bg-gradient-to-br from-purple-900/30 to-gray-900/80 border-purple-700/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Images className="w-5 h-5 text-purple-400" />
                <span className="text-white font-medium">
                  Opplasting ({completedUploads}/{uploadQueue.length} fullført)
                </span>
              </div>
              {!hasActiveUploads && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={clearCompletedUploads}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <X className="w-4 h-4 mr-1" />
                  Fjern fullførte
                </Button>
              )}
            </div>
            
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {uploadQueue.map((item) => (
                <div key={item.id} className="flex items-center gap-3 p-2 bg-gray-800/50 rounded-lg">
                  <img
                    src={item.preview}
                    alt={item.name}
                    className="w-10 h-10 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{item.name}</p>
                    {item.status === 'uploading' && (
                      <Progress value={uploadProgress[item.id] || 0} className="h-1 mt-1" />
                    )}
                    {item.status === 'error' && (
                      <p className="text-xs text-red-400">{item.error}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {item.status === 'pending' && (
                      <Badge variant="outline" className="border-gray-600 text-gray-400">Venter</Badge>
                    )}
                    {item.status === 'uploading' && (
                      <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
                    )}
                    {item.status === 'success' && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                    {item.status === 'error' && (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    )}
                    {(item.status === 'pending' || item.status === 'error') && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFromQueue(item.id)}
                        className="h-8 w-8 p-0 text-gray-400 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {failedUploads > 0 && (
              <p className="text-sm text-red-400 mt-2">
                {failedUploads} {failedUploads === 1 ? 'fil feilet' : 'filer feilet'}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {!pickerMode && user?.role === 'master_admin' && <MediaRestorePanel onDone={fetchImages} />}

      {/* Header */}
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-white flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-purple-400" />
              {pickerMode ? 'Velg bilde' : 'Mediebibliotek'}
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Søk i bilder..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-gray-800 border-gray-600 text-white w-64"
                  data-testid="media-search-input"
                />
              </div>
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="upload-media-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Last opp
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) {
                    handleFilesSelect(Array.from(e.target.files));
                  }
                  e.target.value = ''; // Reset for same file selection
                }}
                data-testid="upload-file-input"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-xl p-8 mb-6 transition-colors ${
              dragActive
                ? 'border-purple-500 bg-purple-500/10'
                : 'border-gray-600 hover:border-gray-500'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{ cursor: 'pointer' }}
            data-testid="media-drop-zone"
          >
            <div className="flex flex-col items-center justify-center text-center">
              <FileImage className="w-12 h-12 text-gray-500 mb-4" />
              <p className="text-gray-300 mb-2">Dra og slipp bilder her</p>
              <p className="text-gray-500 text-sm">eller klikk for å velge filer</p>
              <p className="text-purple-400 text-sm mt-2 font-medium">Du kan velge flere bilder samtidig</p>
              <p className="text-gray-600 text-xs mt-1">Støttede formater: JPG, PNG, GIF, WEBP</p>
            </div>
          </div>

          {/* Image Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Ingen bilder funnet</p>
              <p className="text-gray-500 text-sm mt-2">Last opp ditt første bilde for å komme i gang</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {images.map((image) => (
                <div
                  key={image.id}
                  className={`group relative aspect-square bg-gray-800 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                    recentlyUploaded.includes(image.id)
                      ? 'border-green-500 ring-2 ring-green-500/30'
                      : 'border-transparent hover:border-purple-500'
                  }`}
                  onClick={() => handleImageSelect(image)}
                  data-testid={`media-item-${image.id}`}
                >
                  {/* New upload indicator */}
                  {recentlyUploaded.includes(image.id) && (
                    <div className="absolute top-2 left-2 z-10">
                      <Badge className="bg-green-600 text-white text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Ny
                      </Badge>
                    </div>
                  )}
                  
                  <img
                    src={image.versions?.thumbnail?.url || image.url}
                    alt={image.alt_text || image.original_filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    {pickerMode ? (
                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                        <Check className="w-4 h-4 mr-1" />
                        Velg
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-white/50 text-white hover:bg-white/20"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditOpen(image);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImage(image);
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Image info badge */}
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-white text-xs truncate">{image.title || image.original_filename}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={(open) => {
        setEditModalOpen(open);
        if (!open) setIsNewUpload(false);
      }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              {isNewUpload && <CheckCircle className="w-5 h-5 text-green-500" />}
              {isNewUpload ? 'Bilde lastet opp!' : 'Rediger bildedetaljer'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {isNewUpload 
                ? 'Ditt bilde er nå tilgjengelig. Kontroller innstillingene nedenfor.'
                : 'Oppdater SEO-metadata og se tilgjengelige bildestørrelser'
              }
            </DialogDescription>
          </DialogHeader>

          {selectedImage && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
              {/* Image preview and URLs */}
              <div className="space-y-4">
                <div className="aspect-video bg-gray-800 rounded-lg overflow-hidden">
                  <img
                    src={selectedImage.versions?.medium?.url || selectedImage.url}
                    alt={selectedImage.alt_text || selectedImage.original_filename}
                    className="w-full h-full object-contain"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Tilgjengelige størrelser</Label>
                  <div className="space-y-2">
                    {/* Original */}
                    <div className="flex items-center justify-between p-2 bg-gray-800 rounded-lg">
                      <div>
                        <span className="text-sm text-white">Original</span>
                        <span className="text-xs text-gray-500 ml-2">
                          {selectedImage.width}x{selectedImage.height}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-gray-600 text-gray-300 h-8"
                        onClick={() => copyToClipboard(selectedImage.url, 'original')}
                      >
                        {copiedUrl === `${selectedImage.id}-original` ? (
                          <Check className="w-3 h-3" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </Button>
                    </div>

                    {/* Versions */}
                    {selectedImage.versions && Object.entries(selectedImage.versions).map(([size, data]) => (
                      <div key={size} className="flex items-center justify-between p-2 bg-gray-800 rounded-lg">
                        <div>
                          <span className="text-sm text-white capitalize">{size}</span>
                          <span className="text-xs text-gray-500 ml-2">
                            {data.width}x{data.height}
                          </span>
                          {data.webp_url && (
                            <span className="text-xs text-green-400 ml-2">WebP</span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-gray-600 text-gray-300 h-8"
                          onClick={() => copyToClipboard(data.webp_url || data.url, size)}
                        >
                          {copiedUrl === `${selectedImage.id}-${size}` ? (
                            <Check className="w-3 h-3" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Badge variant="outline" className="border-gray-600 text-gray-400">
                    {formatBytes(selectedImage.size)}
                  </Badge>
                  <Badge variant="outline" className="border-gray-600 text-gray-400">
                    {selectedImage.mime_type}
                  </Badge>
                </div>
              </div>

              {/* Metadata form */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-title" className="text-gray-300">Tittel</Label>
                  <Input
                    id="edit-title"
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    placeholder="Bildetittel"
                    className="bg-gray-800 border-gray-600 text-white"
                    data-testid="edit-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-alt" className="text-gray-300">
                    Alt-tekst (SEO)
                    <span className="text-purple-400 ml-1">*</span>
                  </Label>
                  <Input
                    id="edit-alt"
                    value={editForm.alt_text}
                    onChange={(e) => setEditForm({ ...editForm, alt_text: e.target.value })}
                    placeholder="Beskrivende tekst for skjermlesere og SEO"
                    className="bg-gray-800 border-gray-600 text-white"
                    data-testid="edit-alt-input"
                  />
                  <p className="text-gray-500 text-xs">
                    Viktig for SEO og tilgjengelighet. Beskriv hva bildet viser.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-desc" className="text-gray-300">Beskrivelse</Label>
                  <Textarea
                    id="edit-desc"
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    placeholder="Valgfri beskrivelse av bildet"
                    className="bg-gray-800 border-gray-600 text-white"
                    rows={4}
                    data-testid="edit-desc-input"
                  />
                </div>

                <div className="pt-4 border-t border-gray-700">
                  <p className="text-gray-500 text-sm">
                    Opprinnelig filnavn: <span className="text-gray-300">{selectedImage.original_filename}</span>
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    Lastet opp: <span className="text-gray-300">
                      {selectedImage.created_at ? new Date(selectedImage.created_at).toLocaleString('no-NO') : 'Ukjent'}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="border-red-600 text-red-400 hover:bg-red-600/20"
              onClick={() => {
                setEditModalOpen(false);
                setDeleteConfirmOpen(true);
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Slett bilde
            </Button>
            <div className="flex-1" />
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              className="border-gray-600 text-gray-300"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleEditSave}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-edit-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Lagre endringer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              Bekreft sletting
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Er du sikker på at du vil slette dette bildet? Alle størrelser vil bli permanent fjernet.
            </DialogDescription>
          </DialogHeader>

          {selectedImage && (
            <div className="py-4">
              <div className="flex items-center gap-4 p-4 bg-gray-800 rounded-lg">
                <img
                  src={selectedImage.versions?.thumbnail?.url || selectedImage.url}
                  alt={selectedImage.alt_text}
                  className="w-16 h-16 object-cover rounded"
                />
                <div>
                  <p className="text-white font-medium">{selectedImage.title || selectedImage.original_filename}</p>
                  <p className="text-gray-500 text-sm">{formatBytes(selectedImage.size)}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
              className="border-gray-600 text-gray-300"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Slett permanent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMediaLibrary;
